import { throwError } from "./AuthProvider";
import { UserProfileData } from "./Types";

const endpoint = process.env.REACT_APP_API_END_POINT;
const api_key = process.env.REACT_APP_API_KEY!;
const api_gateway_auth_key = process.env.REACT_APP_API_GATEWAY_AUTH_KEY!;

// 1. account APIs
export async function verifyIPAddressNZ() {
  return await getJsonResponse(`/account/verify-ip-address-nz`, false, 'GET', null, true);
}

export async function registerSPARXUser(registerProps: any) {
  return await getJsonResponse(`/account/register-sparx-user`, false, 'POST', registerProps);
}

export async function signIn(loginProps: any) {
  return await getJsonResponse(`/account/sign-in`, false, 'POST', loginProps, true);
}

export async function getUserProfile(): Promise<UserProfileData> {
  return await getJsonResponse(`/account/my-details`, true);
}

// profile, password and preference will be in the same API
export async function updateUserProfile(userForm: {}) {
  await getJsonResponse(`/account/my-details`, true, 'PUT', userForm);
}

export const deleteMyAccount = async () => {
  await getJsonResponse(`/account/delete-sparx-user`, true, 'DELETE', {});
}

export const sendAccountDeletedFeedback = async (email: string, reasons: string[], details: string) => {
  await getJsonResponse(`/account/feedback`, false, 'POST', { email, reasons, details });
}

export const activateAccount = async (username: string, code: string) => {
  await getJsonResponse(`/account/activate-account`, false, 'POST', { username, code });
}

export const resendConfirmationCode = async (username: string) => {
  await getJsonResponse(`/account/resend-confirmation-code`, false, 'POST', { username });
}

export const resetPassword = async (username: string) => {
  await getJsonResponse(`/account/reset-password`, false, 'POST', { username });
}

export const setNewPassword = async (username: string, code: string, password: string) => {
  await getJsonResponse(`/account/set-new-password`, false, 'POST', { username, code, password });
}

export const unsubscribeEmail = async (email: string, token: string) => {
  await getJsonResponse(`/account/unsubscribe-email?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`, false, 'PUT');
}

// 2. admin APIs
// // @Derek and Mahfuz when allow user register as an admin?
// export async function registerAdmin(registerProps: any) {
//   return await getJsonResponse(`/account/register`, false, 'POST', registerProps);
// }

export const fetchAllUsers = async (page: number, pageSize: number, emailFilter?: string, usernameFilter?: string, createdAtStartTimeFilter?: number, createdAtEndTimeFilter?: number, orderBy?: string, orderType?: string) => {
  // Build the query parameters dynamically
  let queryParams = `page=${page}&pageSize=${pageSize}`;

  if (emailFilter) {
    queryParams += `&email=${encodeURIComponent(emailFilter)}`;
  }

  if (usernameFilter) {
    queryParams += `&username=${encodeURIComponent(usernameFilter)}`;
  }

  if (createdAtStartTimeFilter) {
    queryParams += `&createdAtStartTime=${createdAtStartTimeFilter / 1000}`;
  }

  if (createdAtEndTimeFilter) {
    queryParams += `&createdAtEndTime=${createdAtEndTimeFilter / 1000}`;
  } else if (createdAtStartTimeFilter) {
    // when start time is not empty but end time is, set now time as end time
    queryParams += `&createdAtEndTime=${Math.floor(new Date().getTime() / 1000)}`;
  }

  if (orderBy) {
    queryParams += `&orderBy=${encodeURIComponent(orderBy)}`;
  }

  if (orderType) {
    queryParams += `&orderType=${encodeURIComponent(orderType)}`;
  }

  // Make the API call with the dynamic query parameters
  return await getJsonResponse(`/admin/get-all-sparx-users?${queryParams}`, true);
}

export const deleteUser = async (userUUID: string) => {
  await getJsonResponse(`/admin/delete-sparx-user/${userUUID}`, true, 'DELETE', {});
}

// helpers for get json response
const isJson = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

const requireReLogin = (re = 1) => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');

  // redirect to login page
  window.location.href = `/login?re=${re}`;
}

const updateAccessToken = async (): Promise<string> => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken || refreshToken === 'undefined') {
      console.error("No refresh_token in localStorage");
      requireReLogin();
      return '';
    }

    const { accessToken } = await getJsonResponse(`/account/refresh-token`, false, 'POST', { refreshToken });
    localStorage.setItem('access_token', accessToken);
    return accessToken;
  } catch (error) {
    console.error('Token refresh failed', error);
    requireReLogin();
    return '';
  }
}

const isTokenExpired = (token: string): boolean => {
  if (!token) return true;

  const jwtPayload = JSON.parse(atob(token.split('.')[1]));
  const exp = jwtPayload.exp;
  const now = Math.floor(Date.now() / 1000);

  return exp < (now + 60); // if exp < now + 1 min, it's consider expired
};

export const getValidAccessToken = async (): Promise<string | null> => {
  let accessToken = localStorage.getItem('access_token');
  if (!accessToken || accessToken === 'undefined' || isTokenExpired(accessToken)) {
    return await updateAccessToken();
  }
  return accessToken;
}

export const getJsonResponse = async (
  url: string,
  withAuth: boolean = false,
  method: string = 'GET',
  body: any = null,
  withApiGatewayAuth = false
): Promise<any> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-api-key': api_key,
  };

  if (withAuth) {
    const accessToken = await getValidAccessToken();
    headers['accesstoken'] = `${accessToken}`;
  }

  if (withApiGatewayAuth) {
    // Add Basic auth
    headers['Authorization'] = `Basic ${api_gateway_auth_key}`;
  }

  const options: RequestInit = {
    method,
    headers,
    body: method !== 'GET' ? JSON.stringify(body) : undefined,
  };

  // set timeout
  const controller = new AbortController();
  const signal = controller.signal;
  const timeoutThreshold = 15000; // 15 seconds

  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutThreshold);

  try {
    const response = await fetch(`${endpoint}${url}`, { ...options, signal });
    clearTimeout(timeoutId);

    const responseBody = await response.text(); // Read response as text first

    if (!response.ok) {
      // Check if there's any content to parse as JSON
      if (responseBody) {
        try {
          const errorData = JSON.parse(responseBody);
          throwError(errorData.message || `API request failed with status "${response.status}"`);
        } catch {
          throwError(`API request failed with status "${response.status}" and body: "${responseBody}"`);
        }
      } else {
        throwError(`API request failed with status "${response.status}" and "no content returned"`);
      }
    }

    if (!responseBody || !isJson(responseBody)) {
      return {}; // If the responseBody is empty or not Json, return an empty object or handle accordingly
    }

    return JSON.parse(responseBody); // Only parse if there's something to parse
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error(`API request timeout: "${method} ${url}"`);
      throw new Error(`Request timeout: API request took longer than ${timeoutThreshold / 1000} seconds`);
    }

    console.error(`API request error: "${method} ${url}": `, error);
    throw error; // Rethrow error to handle it further up the call stack
  }
};
